import { StorageKeys } from '~/atoms/StorageKeys'
import {
  DesktopAppMode,
  getDesktopAppMode,
  readDesktopAppDetectionState,
} from '~/pages/Home/Banners/utils/desktopAppDetectionBannerLogicState'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { isElectron } from '~/utils/common'
import {
  buildDeepLinkForPath,
  deepLinkToInAppPath,
  isMainShellPath,
  openDesktopApp,
  toSafeInAppPath,
} from '~/utils/desktopDeepLink'

/** `?web=1` keeps a link in the browser even when the mode is Always. */
export const STAY_IN_BROWSER_PARAM = 'web'
/** Query param on the interstitial naming the route that was handed off. */
export const HANDOFF_TARGET_PARAM = 'to'

/** Public, token or desktop-launch flows that must stay in the browser. */
const BROWSER_ONLY_ROUTES = [
  ApplicationRoutes.LOGIN,
  ApplicationRoutes.SELECT_PBX,
  ApplicationRoutes.FORGOT_PASSWORD,
  '/reset-password',
  '/secure-fax',
  '/virtualfax',
  ApplicationRoutes.OPEN_APP,
  '/desktop-update',
  ApplicationRoutes.OPENED_IN_DESKTOP,
  '/popup',
  '/popup-dev',
  ApplicationRoutes.ICONS,
]

const BROWSER_ONLY_ROUTES_LOWERCASE = BROWSER_ONLY_ROUTES.map((route) =>
  route.toLowerCase(),
)

/**
 * The browser-only route `pathname` falls under (e.g. `/secure-fax` for
 * `/secure-fax/<token>`), or null. Case-insensitive, matching React Router's
 * default route matching. These are also the top-level routes outside the
 * main app shell.
 */
export const findBrowserOnlyRoute = (pathname: string): string | null => {
  const path = pathname.toLowerCase()
  return (
    BROWSER_ONLY_ROUTES_LOWERCASE.find(
      (route) => path === route || path.startsWith(`${route}/`),
    ) ?? null
  )
}

const isBrowserOnlyRoute = (pathname: string) =>
  findBrowserOnlyRoute(pathname) !== null

const isSameOrigin = (referrer: string, origin: string) => {
  if (!referrer) return false
  try {
    return new URL(referrer).origin === origin
  } catch {
    return false
  }
}

export interface DesktopHandoffContext {
  mode: DesktopAppMode
  isElectron: boolean
  /** `PerformanceNavigationTiming.type` of the current document. */
  navigationType: string | undefined
  referrer: string
  origin: string
  alreadyHandledInTab: boolean
  /** Requested route: pathname + search + hash. */
  path: string
}

/**
 * The route to open in the desktop app, or null to stay in the browser. Only a
 * tab freshly opened from outside the app hands off: a plain navigation (not a
 * reload or back/forward) with no same-origin referrer, once per tab, and
 * only for routes the desktop app opens (see `deepLinkListener`).
 */
export const getDesktopHandoffTarget = (
  context: DesktopHandoffContext,
): string | null => {
  if (context.mode !== DesktopAppMode.Always || context.isElectron) return null
  if (context.navigationType !== 'navigate') return null
  if (context.alreadyHandledInTab) return null
  if (isSameOrigin(context.referrer, context.origin)) return null

  const target = toSafeInAppPath(context.path)
  if (!target) return null

  const { pathname, searchParams } = new URL(target, context.origin)
  if (searchParams.get(STAY_IN_BROWSER_PARAM) === '1') return null
  if (isBrowserOnlyRoute(pathname)) return null

  const desktopPath = deepLinkToInAppPath(buildDeepLinkForPath(target))
  if (!desktopPath || !isMainShellPath(desktopPath)) return null

  return target
}

export const buildOpenedInDesktopUrl = (target: string): string =>
  `${ApplicationRoutes.OPENED_IN_DESKTOP}?${new URLSearchParams({
    [HANDOFF_TARGET_PARAM]: target,
  }).toString()}`

/** Marks this tab so it never hands off to the desktop app again. */
export const markDesktopHandoffHandled = (): void => {
  try {
    sessionStorage.setItem(StorageKeys.DesktopAppHandoff, '1')
  } catch {
    // Storage disabled: the navigation-type check still prevents reload loops.
  }
}

const isDesktopHandoffHandled = (): boolean => {
  try {
    return sessionStorage.getItem(StorageKeys.DesktopAppHandoff) === '1'
  } catch {
    return true
  }
}

const readNavigationType = (): string | undefined => {
  const [entry] =
    typeof performance.getEntriesByType === 'function'
      ? performance.getEntriesByType('navigation')
      : []
  return (entry as PerformanceNavigationTiming | undefined)?.type
}

export const readDesktopHandoffContext = (): DesktopHandoffContext => ({
  mode: getDesktopAppMode(readDesktopAppDetectionState()),
  isElectron,
  navigationType: readNavigationType(),
  referrer: document.referrer,
  origin: window.location.origin,
  alreadyHandledInTab: isDesktopHandoffHandled(),
  path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
})

/**
 * Must run before the router is created. When this tab should open in the
 * desktop app, launches it and swaps the URL for the "Opened in the desktop
 * app" interstitial so no web route — including the login redirect — renders.
 * Reloading the interstitial doesn't relaunch: reloads never hand off.
 */
export const redirectToDesktopHandoffIfNeeded = (
  context: DesktopHandoffContext = readDesktopHandoffContext(),
): string | null => {
  const target = getDesktopHandoffTarget(context)
  if (!target) return null

  markDesktopHandoffHandled()
  openDesktopApp(buildDeepLinkForPath(target))
  window.history.replaceState(null, '', buildOpenedInDesktopUrl(target))
  return target
}
